import { isAxiosError } from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import {
  CLARITY_ARTEM_ADMIN_KEY,
  CLARITY_USER_ID_STORAGE_KEY,
} from '@/shared/clarity-session-storage-keys'
import {
  clearBrowserSentryUserContext,
  setBrowserSentryUserContext,
} from '@/shared/lib/reportUnexpectedClientFailure'
import { clarity } from 'react-microsoft-clarity'

export const USER_SESSION_QUERY_KEY = 'session'

export { CLARITY_ARTEM_ADMIN_KEY, CLARITY_USER_ID_STORAGE_KEY }

export type Session = {
  userId: string
  organizationId: string
  lang: string
  isArtemAdmin: boolean
  isReadOnly: boolean
  email: string
  emailVerifiedAt: string | null
  hasPendingEmailVerification: boolean
  /** Present for Lex sessions when the user signed up via WeChat only. */
  isWeChatOnlyUser?: boolean
  mustChangePassword: boolean
}

export const useSignIn = () => {
  return useMutation({
    mutationFn: async (payload: { email: string; password: string }) => {
      const response = await apiClient.post('/v1/auth/login', payload)
      return response.data
    },
  })
}

export type SignUpPayload = {
  firstName: string
  lastName: string
  organizationName: string
  email: string
  password: string
}

export const useSignUp = () => {
  return useMutation({
    mutationFn: async (payload: SignUpPayload) => {
      const response = await apiClient.post('/v1/auth/signup', payload)
      return response.data
    },
  })
}

export function useGetUserSession() {
  return useQuery<Session | null>({
    queryKey: [USER_SESSION_QUERY_KEY],
    queryFn: async (): Promise<Session | null> => {
      try {
        const response = await apiClient.get<Session>('/v1/auth/session')
        const data = response.data
        if (clarity.hasStarted()) {
          const userId = String(data.userId)
          const isArtemAdmin = data.isArtemAdmin

          sessionStorage.setItem(CLARITY_USER_ID_STORAGE_KEY, userId)
          sessionStorage.setItem(
            CLARITY_ARTEM_ADMIN_KEY,
            isArtemAdmin ? 'true' : 'false'
          )

          clarity.setTag('isArtemAdmin', isArtemAdmin ? 'true' : 'false')
          clarity.identify(userId, {
            organizationId: data.organizationId,
          })
        }
        setBrowserSentryUserContext({
          userId: data.userId,
          organizationId: data.organizationId,
        })
        return data
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 401) {
          sessionStorage.removeItem(CLARITY_USER_ID_STORAGE_KEY)
          clearBrowserSentryUserContext()
          return null
        }
        throw error
      }
    },
    throwOnError: (error: unknown) => {
      if (isAxiosError(error) && error.code === 'ERR_NETWORK') {
        return true
      }
      return false
    },
    staleTime: Infinity,
    retry: false,
  })
}

export function useSignOut() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/v1/auth/session')
    },
    onSuccess: () => {
      sessionStorage.removeItem(CLARITY_USER_ID_STORAGE_KEY)
      queryClient.clear()
      window.location.reload()
    },
  })
}
